import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import ColorSwatchPicker from '~components/FormFields/ColorSwatchPicker';
import AutocompleteScopedCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteScopedCompaniesTypeahead';
import TextInput from '~components/FormFields/v2/TextInput';
import { ServiceClass } from '~hooks/useServiceClass/models';
import { useStores } from '~store';
import { DEFAULT_PALETTE_COLOR, paletteColorStyles } from '~utils/colors';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  ServiceClassDto,
  ServiceClassDtoAsyncError,
  serviceClassSchema,
  setServiceClassSchemaDefaultValues,
} from './serviceClassSchema';

export type ServiceClassFormHandler = {
  onSubmit: () => Promise<ServiceClassDto | undefined>;
};

interface ServiceClassFormProps {
  asyncErrors?: ServiceClassDtoAsyncError[];
  serviceClass?: ServiceClass;
}

const ServiceClassForm = forwardRef<ServiceClassFormHandler, ServiceClassFormProps>(
  function ServiceClassForm({ asyncErrors, serviceClass }, ref) {
    const { userStore } = useStores();

    const isEditing = Boolean(serviceClass);

    const { handleSubmit, control, setError, clearErrors } = useForm<ServiceClassDto>({
      resolver: yupResolver(serviceClassSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setServiceClassSchemaDefaultValues(userStore, serviceClass),
    });

    const watchedName = useWatch({ control, name: 'name' });
    const watchedColor = useWatch({ control, name: 'color' });

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => {
                resolve(data);
              },
              () => {
                resolve(undefined);
              },
            )();
          });
        },
      }),
      [handleSubmit],
    );

    useEffect(() => {
      if (asyncErrors) {
        for (const error of asyncErrors) {
          setError(error.field as keyof ServiceClassDto, { message: error.message });
        }
      }

      return () => {
        clearErrors();
      };
    }, [asyncErrors]);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
        <Box>
          <AutocompleteScopedCompaniesTypeahead
            companyId={getEffectiveUserCompanyId(userStore)}
            control={control}
            disableClearable
            isDisabled={isEditing}
            isRequired
            label={t('form_fields.company')}
            name="company"
          />
        </Box>

        <Box>
          <TextInput
            control={control}
            isRequired
            label={t('form_fields.name')}
            name="name"
          />
        </Box>

        <Box>
          <Box sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
            {t('form_fields.color')}
          </Box>
          <ColorSwatchPicker control={control} name="color" />
        </Box>

        <Box sx={{ mt: 1 }}>
          <Box sx={{ mb: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
            {t('common.preview')}
          </Box>
          <Box
            component="span"
            data-test-id="service-class-form-preview"
            sx={{
              ...paletteColorStyles(watchedColor || DEFAULT_PALETTE_COLOR),
              borderRadius: 1,
              display: 'inline-block',
              fontWeight: 600,
              px: 1,
              py: 0.5,
            }}
          >
            {watchedName || t('form_fields.name')}
          </Box>
        </Box>
      </Box>
    );
  },
);

export default ServiceClassForm;
